import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ClipboardList, Hash, Lock, MessageCircle, Plus, RefreshCw, Rocket, Send, Target } from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRIORITY_OPTIONS = ['media', 'alta', 'urgente'];
const OBJECTIVE_OPTIONS = ['crecimiento', 'ventas', 'marca', 'automatizacion', 'mercado', 'soporte', 'producto'];
const EXECUTION_OPTIONS = ['manual', 'asistida', 'auto_dispatch'];
const STATUS_OPTIONS = ['pendiente', 'despachada', 'en_proceso', 'bloqueada', 'completada'];
const ROADMAP_OPTIONS = ['draft', 'active', 'pending_human', 'executing', 'blocked', 'completed'];
const VALIDATION_OPTIONS = ['pending_human', 'approved', 'rejected'];
const STATUS_META = {
  pendiente: { label: 'Inbox', accent: 'text-amber-200', chip: 'border-amber-400/20 bg-amber-500/10 text-amber-200' },
  despachada: { label: 'Despachado', accent: 'text-sky-200', chip: 'border-sky-400/20 bg-sky-500/10 text-sky-200' },
  en_proceso: { label: 'En ejecución', accent: 'text-primary', chip: 'border-primary/20 bg-primary/10 text-primary' },
  bloqueada: { label: 'Bloqueado', accent: 'text-rose-200', chip: 'border-rose-400/20 bg-rose-500/10 text-rose-200' },
  completada: { label: 'Hecho', accent: 'text-emerald-200', chip: 'border-emerald-400/20 bg-emerald-500/10 text-emerald-200' },
  pending_human: { label: 'Espera humana', accent: 'text-amber-200', chip: 'border-amber-400/20 bg-amber-500/10 text-amber-200' },
  executing: { label: 'Ejecutando', accent: 'text-primary', chip: 'border-primary/20 bg-primary/10 text-primary' },
  blocked: { label: 'Bloqueado', accent: 'text-rose-200', chip: 'border-rose-400/20 bg-rose-500/10 text-rose-200' },
  completed: { label: 'Hecho', accent: 'text-emerald-200', chip: 'border-emerald-400/20 bg-emerald-500/10 text-emerald-200' },
  active: { label: 'Activo', accent: 'text-sky-200', chip: 'border-sky-400/20 bg-sky-500/10 text-sky-200' },
  draft: { label: 'Borrador', accent: 'text-text-muted', chip: 'border-border-subtle bg-card/40 text-text-muted' },
};

const MetricCard = ({ label, value, helper, testId }) => (
  <div className="glass-card tracing-panel p-5" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{label}</p>
    <p className="mt-3 text-3xl font-bold text-text-heading">{value}</p>
    <p className="mt-2 text-xs text-text-muted">{helper}</p>
  </div>
);

export default function CEOOrdersHub({ authFetch }) {
  const [hub, setHub] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [form, setForm] = useState({
    title: '',
    instruction: '',
    company_name: '',
    objective_type: 'crecimiento',
    priority: 'alta',
    selected_agents: 'COMERCIAL,VENTAS,CONTENIDO,ADMIN',
    channels: 'instagram,facebook,linkedin',
    execution_mode: 'auto_dispatch',
    expected_outcome: '',
    notes: '',
    client_email: ''
  });

  const loadHub = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/admin/ceo-orders`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo cargar el centro de órdenes del CEO');
      setHub(data);
    } catch (error) {
      toast.error(error.message || 'No se pudo cargar el centro de órdenes del CEO');
    }
  }, [authFetch]);

  useEffect(() => {
    loadHub();
  }, []);

  const topObjective = useMemo(() => {
    const item = hub?.objective_breakdown?.slice().sort((a, b) => b.count - a.count)[0];
    return item?.objective_type || 'crecimiento';
  }, [hub]);

  const ordersByStatus = useMemo(() => {
    const map = { pendiente: [], despachada: [], en_proceso: [], bloqueada: [], completada: [] };
    (hub?.orders || []).forEach((order) => {
      if (!map[order.status]) map[order.status] = [];
      map[order.status].push(order);
    });
    return map;
  }, [hub]);

  const channelRows = useMemo(() => ([
    { id: 'ceo-despacho', label: '#ceo-despacho', helper: `${hub?.metrics?.pending || 0} pendientes`, active: true },
    { id: 'briefings', label: '#briefings', helper: `${hub?.metrics?.completed || 0} cerradas`, active: true },
    { id: 'whatsapp', label: '#whatsapp', helper: hub?.whatsapp?.status || 'pending_credentials', active: !!hub?.whatsapp?.received_commands },
    { id: 'growth', label: '#growth', helper: `${topObjective} como foco actual`, active: true },
  ]), [hub, topObjective]);

  const createOrder = async (event) => {
    event.preventDefault();
    try {
      setSubmitting(true);
      const payload = {
        ...form,
        selected_agents: form.selected_agents.split(',').map((item) => item.trim()).filter(Boolean),
        channels: form.channels.split(',').map((item) => item.trim()).filter(Boolean),
      };
      const res = await authFetch(`${API}/admin/ceo-orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo crear la orden');
      toast.success('Orden CEO enviada al sistema');
      setForm({ title: '', instruction: '', company_name: '', objective_type: 'crecimiento', priority: 'alta', selected_agents: 'COMERCIAL,VENTAS,CONTENIDO,ADMIN', channels: 'instagram,facebook,linkedin', execution_mode: 'auto_dispatch', expected_outcome: '', notes: '', client_email: '' });
      loadHub();
    } catch (error) {
      toast.error(error.message || 'No se pudo crear la orden');
    } finally {
      setSubmitting(false);
    }
  };

  const updateOrder = async (orderId, payload) => {
    try {
      setSavingId(orderId);
      const res = await authFetch(`${API}/admin/ceo-orders/${orderId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo actualizar la orden');
      toast.success('Orden actualizada');
      loadHub();
    } catch (error) {
      toast.error(error.message || 'No se pudo actualizar la orden');
    } finally {
      setSavingId(null);
    }
  };

  const updateRoadmapTask = async (taskId, payload) => {
    try {
      setSavingId(taskId);
      const res = await authFetch(`${API}/admin/tasks/${taskId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo actualizar la tarea del roadmap');
      toast.success(payload.validation_status === 'approved' ? 'Tarea aprobada y roadmap reanudado' : 'Tarea del roadmap actualizada');
      loadHub();
    } catch (error) {
      toast.error(error.message || 'No se pudo actualizar la tarea del roadmap');
    } finally {
      setSavingId(null);
    }
  };

  if (!hub) return <div className="glass-card p-8 text-center text-text-muted" data-testid="ceo-orders-loading">Cargando órdenes del CEO...</div>;

  return (
    <div className="space-y-6" data-testid="ceo-orders-hub">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Centro de mando · Trello + Slack</p>
          <h3 className="mt-2 text-2xl font-semibold text-text-heading">Despachar, coordinar y seguir órdenes en una sola vista operativa</h3>
          <p className="mt-2 max-w-3xl text-sm text-text-body">Pensado para sentirse como un panel de mando: tablero visual por estado, bandeja de actividad, canales vivos y trazabilidad clara para cada decisión.</p>
        </div>
        <button onClick={loadHub} className="btn-secondary" data-testid="ceo-orders-refresh-button"><RefreshCw className="w-4 h-4" /> Refrescar</button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Total" value={hub.metrics.total} helper="Órdenes registradas" testId="ceo-orders-metric-total" />
        <MetricCard label="Despachadas" value={hub.metrics.dispatched} helper="Ya activadas" testId="ceo-orders-metric-dispatched" />
        <MetricCard label="Aprobación humana" value={hub.metrics.roadmaps_pending_human || 0} helper="Roadmaps esperando validación" testId="ceo-orders-metric-pending-human" />
        <MetricCard label="Foco top" value={topObjective} helper={`${hub.metrics.roadmaps_completed || 0} roadmaps completados`} testId="ceo-orders-metric-objective" />
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.76fr_1.24fr]">
        <div className="space-y-6">
          <form onSubmit={createOrder} className="glass-card tracing-panel p-6 space-y-4" data-testid="ceo-orders-create-form">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Composer</p>
                <h4 className="mt-2 text-xl font-semibold text-text-heading">Nueva orden del CEO</h4>
              </div>
              <Plus className="w-5 h-5 text-primary" />
            </div>
            <input className="input-kinesthetic" placeholder="Título de la orden" value={form.title} onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))} data-testid="ceo-order-title-input" />
            <textarea className="input-kinesthetic resize-none" rows={4} placeholder="Instrucción del CEO" value={form.instruction} onChange={(e) => setForm((prev) => ({ ...prev, instruction: e.target.value }))} data-testid="ceo-order-instruction-input" />
            <input className="input-kinesthetic" placeholder="Empresa / cuenta objetivo" value={form.company_name} onChange={(e) => setForm((prev) => ({ ...prev, company_name: e.target.value }))} data-testid="ceo-order-company-input" />

            <div className="grid gap-4 md:grid-cols-3">
              <select className="input-kinesthetic" value={form.objective_type} onChange={(e) => setForm((prev) => ({ ...prev, objective_type: e.target.value }))} data-testid="ceo-order-objective-select">{OBJECTIVE_OPTIONS.map((item) => <option key={item} value={item}>{item}</option>)}</select>
              <select className="input-kinesthetic" value={form.priority} onChange={(e) => setForm((prev) => ({ ...prev, priority: e.target.value }))} data-testid="ceo-order-priority-select">{PRIORITY_OPTIONS.map((item) => <option key={item} value={item}>{item}</option>)}</select>
              <select className="input-kinesthetic" value={form.execution_mode} onChange={(e) => setForm((prev) => ({ ...prev, execution_mode: e.target.value }))} data-testid="ceo-order-execution-select">{EXECUTION_OPTIONS.map((item) => <option key={item} value={item}>{item}</option>)}</select>
            </div>

            <input className="input-kinesthetic" placeholder="Agentes (coma)" value={form.selected_agents} onChange={(e) => setForm((prev) => ({ ...prev, selected_agents: e.target.value.toUpperCase() }))} data-testid="ceo-order-agents-input" />
            <input className="input-kinesthetic" placeholder="Canales (coma)" value={form.channels} onChange={(e) => setForm((prev) => ({ ...prev, channels: e.target.value }))} data-testid="ceo-order-channels-input" />
            <input className="input-kinesthetic" placeholder="Email cliente para reflejar progreso" value={form.client_email} onChange={(e) => setForm((prev) => ({ ...prev, client_email: e.target.value }))} data-testid="ceo-order-client-email-input" />
            <textarea className="input-kinesthetic resize-none" rows={2} placeholder="Resultado esperado" value={form.expected_outcome} onChange={(e) => setForm((prev) => ({ ...prev, expected_outcome: e.target.value }))} data-testid="ceo-order-outcome-input" />
            <textarea className="input-kinesthetic resize-none" rows={2} placeholder="Notas" value={form.notes} onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))} data-testid="ceo-order-notes-input" />
            <button type="submit" className="btn-primary w-full justify-center" disabled={submitting} data-testid="ceo-order-submit-button">
              <Send className="w-4 h-4" /> {submitting ? 'Enviando...' : 'Crear orden'}
            </button>
          </form>

          <div className="glass-card tracing-panel p-6" data-testid="ceo-orders-slack-channels">
            <div className="flex items-center gap-2 text-text-heading">
              <Hash className="w-5 h-5 text-primary" />
              <h4 className="text-lg font-semibold">Canales vivos</h4>
            </div>
            <div className="mt-4 space-y-3">
              {channelRows.map((item) => (
                <div key={item.id} className="rounded-[20px] border border-border-subtle bg-background/55 px-4 py-3" data-testid={`ceo-orders-channel-${item.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-text-heading">{item.label}</p>
                    <span className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${item.active ? 'border border-primary/20 bg-primary/10 text-primary' : 'border border-border-subtle text-text-muted'}`}>{item.active ? 'activo' : 'pasivo'}</span>
                  </div>
                  <p className="mt-1 text-sm text-text-muted">{item.helper}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="glass-card tracing-panel p-6" data-testid="ceo-orders-whatsapp-status">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Canal WhatsApp</p>
                <h4 className="mt-2 text-xl font-semibold text-text-heading">Despacho desde tu móvil</h4>
              </div>
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary"><MessageCircle className="w-5 h-5" /></div>
            </div>
            <div className="mt-5 grid gap-4 md:grid-cols-3 text-sm">
              <div className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid="ceo-orders-whatsapp-state-value"><p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Estado</p><p className="mt-2 text-text-heading font-semibold">{hub.whatsapp?.status || 'pending_credentials'}</p></div>
              <div className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid="ceo-orders-whatsapp-received"><p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Mensajes</p><p className="mt-2 text-text-heading font-semibold">{hub.whatsapp?.received_commands || 0}</p></div>
              <div className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid="ceo-orders-whatsapp-routed"><p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Órdenes</p><p className="mt-2 text-text-heading font-semibold">{hub.whatsapp?.routed_orders || 0}</p></div>
            </div>
            <p className="mt-4 text-sm text-text-body" data-testid="ceo-orders-whatsapp-next-step">{hub.whatsapp?.next_step || 'Pendiente configurar Meta App + webhook.'}</p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="glass-card tracing-panel p-6" data-testid="ceo-orders-kanban-board">
            <div className="flex items-center justify-between gap-3 mb-5">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Board operativo</p>
                <h4 className="mt-2 text-xl font-semibold text-text-heading">Tablero de órdenes por estado</h4>
              </div>
              <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.16em]">
                {hub.source_breakdown?.map((item) => <span key={item.source} className="rounded-full border border-border-subtle px-2 py-1 text-text-muted">{item.source}: {item.count}</span>)}
              </div>
            </div>

            <div className="grid gap-4 xl:grid-cols-5" data-testid="ceo-orders-kanban-columns">
              {STATUS_OPTIONS.map((status) => (
                <div key={status} className="space-y-3 min-w-0" data-testid={`ceo-orders-kanban-${status}`}>
                  <div className="flex items-center justify-between gap-2 px-1">
                    <p className={`text-sm font-semibold ${STATUS_META[status].accent}`}>{STATUS_META[status].label}</p>
                    <span className="text-xs text-text-muted">{ordersByStatus[status]?.length || 0}</span>
                  </div>
                  <div className="space-y-3 min-h-[140px]">
                    {(ordersByStatus[status] || []).map((order) => (
                      <div key={order.id} className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid={`ceo-order-card-${order.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{order.objective_type}</p>
                            <h5 className="mt-2 text-sm font-semibold text-text-heading line-clamp-2">{order.title}</h5>
                          </div>
                          <span className={`rounded-full border px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${STATUS_META[order.status]?.chip || STATUS_META.pendiente.chip}`}>{order.priority}</span>
                        </div>
                        <p className="mt-3 text-sm text-text-body line-clamp-4 whitespace-pre-wrap">{order.instruction}</p>
                        <div className="mt-3 flex flex-wrap gap-2" data-testid={`ceo-order-agents-${order.id}`}>{(order.selected_agents || []).map((item) => <span key={item} className="rounded-full border border-primary/20 bg-primary/10 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-primary">{item}</span>)}</div>
                        <div className="mt-3 text-xs text-text-muted space-y-1">
                          <p>Empresa: {order.company_name || 'Neuralia Core'}</p>
                          <p className="mt-1">Task: {order.linked_task_id || 'sin task'}</p>
                          <p data-testid={`ceo-order-roadmap-status-${order.id}`}>Roadmap: {order.roadmap_status || 'draft'} · {(order.roadmap_summary || {}).completed || 0}/{(order.roadmap_summary || {}).total || 0}</p>
                        </div>
                        <div className="mt-4 space-y-3">
                          <select className="input-kinesthetic min-w-[160px]" value={order.status} onChange={(e) => updateOrder(order.id, { status: e.target.value })} data-testid={`ceo-order-status-${order.id}`}>
                            {STATUS_OPTIONS.map((item) => <option key={item} value={item}>{item}</option>)}
                          </select>
                          <select className="input-kinesthetic min-w-[160px]" value={order.roadmap_status || 'draft'} onChange={(e) => updateOrder(order.id, { roadmap_status: e.target.value })} data-testid={`ceo-order-roadmap-select-${order.id}`}>
                            {ROADMAP_OPTIONS.map((item) => <option key={item} value={item}>{item}</option>)}
                          </select>
                          <textarea className="input-kinesthetic resize-none" rows={2} defaultValue={order.notes || ''} onBlur={(e) => updateOrder(order.id, { notes: e.target.value })} data-testid={`ceo-order-notes-${order.id}`} />
                          {!!order.roadmap_tasks?.length && (
                            <div className="rounded-[18px] border border-primary/15 bg-primary/5 p-3 space-y-3" data-testid={`ceo-order-roadmap-tasks-${order.id}`}>
                              <div className="flex items-center gap-2 text-text-heading">
                                <ClipboardList className="w-4 h-4 text-primary" />
                                <p className="text-sm font-semibold">Roadmap ejecutable</p>
                              </div>
                              {order.roadmap_tasks.map((task) => (
                                <div key={task.id} className="rounded-[16px] border border-border-subtle bg-background/50 p-3" data-testid={`ceo-roadmap-task-${task.id}`}>
                                  <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                      <p className="text-sm font-semibold text-text-heading">Paso {task.roadmap_step} · {task.title}</p>
                                      <p className="mt-1 text-xs text-text-muted">{task.priority} · {task.deadline ? new Date(task.deadline).toLocaleDateString('es-ES') : 'sin deadline'}</p>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      <span className="rounded-full border border-border-subtle px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-text-muted">{task.status}</span>
                                      {task.validation_required && <span className="rounded-full border border-amber-400/20 bg-amber-500/10 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-amber-200">validación humana</span>}
                                    </div>
                                  </div>
                                  {!!task.dependencies?.length && <p className="mt-2 text-xs text-text-muted">Depende de: {task.dependencies.length} tarea(s)</p>}
                                  {!!task.validation_checklist?.length && (
                                    <div className="mt-3 space-y-1" data-testid={`ceo-roadmap-task-checklist-${task.id}`}>
                                      {task.validation_checklist.map((item, index) => (
                                        <p key={`${task.id}-${index}`} className="text-xs text-text-body flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-primary" /> {item}</p>
                                      ))}
                                    </div>
                                  )}
                                  {task.validation_status === 'pending_human' && (
                                    <div className="mt-3 flex flex-wrap gap-2" data-testid={`ceo-roadmap-task-actions-${task.id}`}>
                                      {VALIDATION_OPTIONS.map((option) => (
                                        <button key={option} type="button" onClick={() => updateRoadmapTask(task.id, { validation_status: option })} className="btn-secondary text-xs py-2 px-3" data-testid={`ceo-roadmap-task-${option}-${task.id}`}>
                                          {option === 'approved' ? 'Aprobar' : option === 'rejected' ? 'Rechazar' : 'Mantener en revisión'}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          {savingId === order.id && <p className="text-xs text-primary">Guardando cambios...</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="glass-card tracing-panel p-6" data-testid="ceo-orders-whatsapp-feed">
            <div className="flex items-center gap-2 text-text-heading">
              <MessageCircle className="w-5 h-5 text-primary" />
              <h4 className="text-lg font-semibold">Feed de actividad</h4>
            </div>
            <div className="mt-4 space-y-3">
              {(hub.recent_whatsapp_commands || []).map((item) => (
                <div key={item.id} className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`ceo-orders-whatsapp-command-${item.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-text-heading">{item.sender_phone || 'WhatsApp'}</p>
                      <p className="mt-1 text-sm text-text-body whitespace-pre-wrap">{item.command_text || 'Sin texto visible.'}</p>
                    </div>
                    <span className="rounded-full border border-border-subtle px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-text-muted">{item.status}</span>
                  </div>
                  <p className="mt-2 text-xs text-text-muted">Orden ligada: {item.linked_order_id || 'todavía no'}</p>
                </div>
              ))}
              {!(hub.recent_whatsapp_commands || []).length && <p className="text-sm text-text-muted" data-testid="ceo-orders-whatsapp-empty">Aún no han entrado órdenes desde WhatsApp.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}